import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { MapPin, Users, GraduationCap, Globe } from "lucide-react";
import { api } from "@/lib/api";
import LifestyleExtras from "./LifestyleExtras";
import CampusVideoTour from "@/components/app/CampusVideoTour";

/**
 * TabLife — enriched "Living there" panel for the University Detail
 * page. Replaces the plain city / campus / weather / fit-score list
 * with a visual-density lift: a Google Places hero photo (already
 * fetched by our Places daemon), a campus map link, and a student-mix
 * ring + composition strip so a family can see the shape of the
 * cohort at a glance without hunting through numbered rows.
 *
 * All data is uni-level and already on the `uni` doc — nothing new
 * is scraped or computed here.
 */

function pct(n) {
  if (n == null) return null;
  return Math.round(Number(n) * 100);
}

function StudentMixRing({ ugPct, pgPct, intlPct, total }) {
  // Three concentric rings tell three separate stories:
  //   outer   — UG vs PG mix
  //   middle  — international share of the whole cohort
  //   inner   — a single "total students" number as the anchor
  // Missing data collapses cleanly to a "—" state.
  if (!total && ugPct == null && intlPct == null) return null;
  const ug = ugPct != null ? ugPct : (pgPct != null ? 100 - pgPct : 65);
  const pg = 100 - ug;
  const intl = intlPct != null ? intlPct : 0;
  const size = 148;
  const r1 = 68;
  const r2 = 52;
  const c1 = 2 * Math.PI * r1;
  const c2 = 2 * Math.PI * r2;

  return (
    <div className="flex items-center gap-6" data-testid="student-mix-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* outer ring — UG vs PG */}
        <circle cx={size/2} cy={size/2} r={r1} fill="none" stroke="#F3F1EC" strokeWidth="12" />
        <circle
          cx={size/2} cy={size/2} r={r1} fill="none"
          stroke="#002147" strokeWidth="12"
          strokeDasharray={`${(ug/100)*c1} ${c1}`}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          strokeLinecap="butt"
        />
        {/* middle ring — international */}
        <circle cx={size/2} cy={size/2} r={r2} fill="none" stroke="#F3F1EC" strokeWidth="10" />
        <circle
          cx={size/2} cy={size/2} r={r2} fill="none"
          stroke="#7C3AED" strokeWidth="10"
          strokeDasharray={`${(intl/100)*c2} ${c2}`}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          strokeLinecap="butt"
        />
        {/* inner label */}
        <text x={size/2} y={size/2 - 4} textAnchor="middle" className="font-mono font-semibold" style={{fontSize: 20, fill: "#002147"}}>
          {total ? total.toLocaleString() : "—"}
        </text>
        <text x={size/2} y={size/2 + 14} textAnchor="middle" className="font-mono" style={{fontSize: 9, fill: "#78716C", letterSpacing: 1}}>
          STUDENTS
        </text>
      </svg>
      <div className="text-xs space-y-2">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#002147]" />
          <span className="font-underground-sc tracking-wider text-[10px] text-stone-500">Undergraduate</span>
          <span className="font-mono text-stone-900 font-semibold">{ug}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-stone-300" />
          <span className="font-underground-sc tracking-wider text-[10px] text-stone-500">Postgraduate</span>
          <span className="font-mono text-stone-900 font-semibold">{pg}%</span>
        </div>
        <div className="flex items-center gap-2 pt-1 border-t border-stone-200">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#7C3AED]" />
          <span className="font-underground-sc tracking-wider text-[10px] text-stone-500">International</span>
          <span className="font-mono text-stone-900 font-semibold">{intl}%</span>
        </div>
      </div>
    </div>
  );
}

export default function TabLife({ uni, format }) {
  const total = uni.total_students;
  const ugPct = pct(1 - (uni.postgrad_pct || 0));  // implied when we have postgrad_pct
  const pgPct = pct(uni.postgrad_pct);
  const intlPct = pct(uni.international_pct);
  const costOfLiving = format(
    (uni.accommodation_annual_usd || 0) + (uni.food_annual_usd || 0),
  );
  const mapsHref = uni.place_id
    ? `https://www.google.com/maps/place/?q=place_id:${uni.place_id}`
    : `https://www.google.com/maps/search/${encodeURIComponent(`${uni.name}, ${uni.city || ""}`)}`;
  const heroPhoto = uni.photo_url || uni.image_url;

  return (
    <div className="space-y-8" data-testid="tab-life">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#7C3AED] block">
        01 · Living There
      </span>
      <h2 className="font-editorial text-2xl md:text-3xl text-[#002147] leading-tight border-b border-[#002147]/10 pb-4">
        The cohort, the campus, and the city
      </h2>

      {/* 90-second campus tour — renders nothing when the uni has no
          curated video, so pages stay honest for the long tail. Sits
          FIRST in the right column so a family gets the vibe before
          they parse a single number. Managed via CMS at
          `/admin/ops/campus-videos`. */}
      {uni?.campus_video_url ? (
        <CampusVideoTour
          videoUrl={uni.campus_video_url}
          uniName={uni.name}
          city={uni.city}
        />
      ) : null}

      {/* Editorial hero photo + companion map iframe.
          Photo answers "what does the campus look like"; map answers
          "where is it". Google's classic embed URL is used because it
          needs no API-key activation — the modern Embed API requires
          the same "Maps Embed API" scope we haven't turned on yet on
          the Places key. Both signals scroll together. */}
      {heroPhoto && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="life-hero-photo">
          <div className="relative overflow-hidden bg-[#002147]">
            <img
              src={heroPhoto}
              alt={`${uni.name} campus`}
              className="w-full h-64 md:h-80 object-cover grayscale-[15%] sepia-[8%]"
              loading="lazy"
            />
            {uni.photo_attributions?.[0] && (
              <a
                href={uni.photo_attributions[0].uri}
                target="_blank" rel="noopener noreferrer"
                className="absolute bottom-3 right-3 bg-[#F3F1EC]/95 backdrop-blur-sm text-[#002147] text-[10px] font-underground-sc tracking-widest px-3 py-1.5 hover:bg-[#F3F1EC] transition"
              >
                Photo · {uni.photo_attributions[0].displayName}
              </a>
            )}
            <span className="absolute top-3 left-3 bg-[#002147] text-[#F3F1EC] px-3 py-1 text-[10px] font-underground-sc tracking-widest">
              Campus
            </span>
          </div>
          <div className="relative overflow-hidden border border-[#002147]/10 bg-[#F3F1EC]">
            <iframe
              title={`${uni.name} map`}
              src={`https://maps.google.com/maps?q=${encodeURIComponent(
                (uni.name || "") + (uni.city ? ", " + uni.city : "")
              )}&z=15&output=embed`}
              className="w-full h-64 md:h-80 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="life-map-embed"
            />
            <span className="absolute top-3 left-3 bg-[#7C3AED] text-white px-3 py-1 text-[10px] font-underground-sc tracking-widest z-10 pointer-events-none">
              Location
            </span>
            <a
              href={mapsHref}
              target="_blank" rel="noopener noreferrer"
              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-[#002147] text-[#F3F1EC] px-3 py-1.5 text-[10px] font-underground-sc tracking-widest hover:bg-[#001030] transition"
              data-testid="life-open-in-maps"
            >
              <MapPin className="h-3 w-3" /> Open in Maps
            </a>
          </div>
        </div>
      )}

      {/* 12-month climate sparkline + housing guarantee card. */}
      <LifestyleExtras uni={uni} format={format} />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        <div className="md:col-span-8 p-8 bg-white/50 border border-[#002147]/10">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#7C3AED] block mb-2">02 · The Cohort</span>
          <h3 className="font-editorial text-xl text-[#002147] mb-6">Who you'd sit alongside</h3>
          <StudentMixRing ugPct={ugPct} pgPct={pgPct} intlPct={intlPct} total={total} />
          <p className="mt-6 text-sm font-sans text-[#002147]/80 leading-relaxed border-t border-[#002147]/10 pt-4 max-w-xl">
            Undergraduate share (outer ring) is what you'll sit alongside in lectures.
            The purple middle ring is the international share — a proxy for classroom
            diversity and the community size you'd join if applying from overseas.
          </p>
        </div>

        <div className="md:col-span-4 p-8 bg-[#002147] text-[#F3F1EC]">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#7C3AED] block mb-2">03 · Personal Fit</span>
          <h3 className="font-editorial text-xl mb-6">Signals</h3>
          <div className="space-y-3">
            {[
              ["Diversity", uni.fit_scores?.diversity],
              ["Sport",     uni.fit_scores?.sport],
              ["City",      uni.fit_scores?.city],
              ["Campus",    uni.fit_scores?.campus],
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between border-b border-[#F3F1EC]/10 pb-2 last:border-0">
                <span className="font-sans text-sm text-[#F3F1EC]/70">{k}</span>
                <span className="font-mono text-xl">
                  {v ?? 0}<span className="text-[#F3F1EC]/40 text-xs ml-0.5">/10</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#7C3AED] block mb-2">04 · Practicalities</span>
        <h3 className="font-editorial text-xl text-[#002147] mb-4">Where you'd live</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-b border-[#002147]/20 divide-x divide-[#002147]/10">
          {[
            ["City", uni.city],
            ["Campus type", uni.campus_type],
            ["Weather", uni.weather],
            ["Cost of living / yr", costOfLiving],
          ].map(([k, v], i) => (
            <div key={k} className="p-5" data-testid={i === 3 ? "life-cost-of-living" : undefined}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">{k}</div>
              <div className={`font-serif text-lg mt-2 ${i === 3 ? "text-[#7C3AED]" : "text-[#002147]"}`}>
                {v || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
